import React, { useState, useEffect } from 'react';
import validationErrors from '@/lib/validationErrors';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getByTableIdEdahabPaymentService,
  getByTableIdRaamsPaymentService,
} from '@/services/PaymentService';
import { getTableBySlugDiningTableService } from '@/services/DiningTableServices';
import { setBranchId, setCompanyId } from '@/security/Axios';

const PaymentCheckPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [statusText, setStatusText] = useState('pending');
  const { slug, id } = useParams();
  const gateway = String(searchParams.get('gateway') || 'edahab').toLowerCase();

  useEffect(() => {
    if (slug && id && id !== '') {
      // 1) Hydrate tenant headers from table slug (important after external redirect)
      hydrateTenantFromSlugAndCheck(String(slug), String(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, id]);

  const hydrateTenantFromSlugAndCheck = (slugVal: string, orderId: string) => {
    setIsLoading(true);
    dispatch(getTableBySlugDiningTableService(slugVal))
      .then((res: any) => {
        const table = res?.data?.data;
        const bId = String(table?.branch_id?._id || table?.branch_id || '');
        const cId = String(table?.company_id?._id || table?.company_id || '');
        if (cId) setCompanyId(cId);
        if (bId) setBranchId(bId);
      })
      .catch(({ message, errorData }: any) => {
        // still try payment status even if slug fetch failed
        validationErrors(errorData, message);
      })
      .finally(() => {
        // 2) Now check payment status
        getPaymentStatusCheck(orderId);
      });
  };

  const getPaymentStatusCheck = (orderId: string) => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;

    const verifyThunk = gateway === 'raams'
      ? () => dispatch(getByTableIdRaamsPaymentService(orderId))
      : () => dispatch(getByTableIdEdahabPaymentService(orderId));

    const runCheck = () => {
      attempts += 1;
      verifyThunk()
        .then((res: any) => {
          if (cancelled) return;

          const data = res?.data?.data || {};
          if (data?.isPaid || data?.status === 'paid') {
            setStatusText('paid');
            setCountdown(5);
            setIsLoading(false);
            return;
          }

          if (data?.status === 'failed') {
            setStatusText('failed');
            setCountdown(5);
            setIsLoading(false);
            return;
          }

          setStatusText('pending');

          if (attempts >= maxAttempts) {
            setCountdown(5);
            setIsLoading(false);
            return;
          }

          setTimeout(runCheck, 3000);
        })
        .catch(({ message, errorData }: any) => {
          if (cancelled) return;
          validationErrors(errorData, message);
          setCountdown(5);
          setIsLoading(false);
        });
    };

    runCheck();
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (countdown > 0 && !isLoading) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(`/menu/table-order/${slug}/${id}`);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown, isLoading, navigate, slug, id]);

  return (
    <section className="py-14 px-4 w-full max-w-3xl mx-auto">
      <h3 className="capitalize text-base font-medium text-center mt-2 mb-3">
        Thank you for your order!
      </h3>
      <img className="w-[120px] mx-auto mb-3" src={'/assets/images/cart/confirm.gif'} alt="gif" />
      <h3 className="capitalize text-lg font-medium text-center mb-3 text-primary">
        Order Payment
      </h3>
      <p className="text-sm leading-6 mb-4">
        {statusText === 'paid'
          ? 'Your payment was confirmed successfully.'
          : statusText === 'failed'
          ? 'Your payment did not complete. You can try again from the order page.'
          : 'Your order payment is currently pending. Please wait for verification.'}
        <br />
        <b className="font-medium">You will be redirected in {countdown} seconds.</b>
      </p>
    </section>
  );
};

export default PaymentCheckPage;
